import { useState, MouseEvent, ChangeEvent, ReactNode } from 'react';
import { CompositeDecorator, Editor, EditorState, RichUtils, Modifier, convertToRaw } from 'draft-js';
import 'draft-js/dist/Draft.css';
import { Box, Grid } from '@mui/material';
import { RichTextControls } from '@components/richTextEditor/RichTextControls';
import { AddLinkDialog } from '@components/richTextEditor/dialogs/AddLinkDialog';
import { URLValue } from '@components/richTextEditor/URLValue.type';
import { containerEditor } from './RichTextEditor.styles';
import { useConvertEditorState } from './RichTextEditor.hooks';

type LinkType = {
  contentState: any;
  entityKey: string;
  decoratedText: string;
  children: ReactNode;
};

function findLinkEntities(contentBlock: any, callback: Function, contentState: any) {
  contentBlock.findEntityRanges((character: any) => {
    const entityKey = character.getEntity();
    return entityKey !== null && contentState.getEntity(entityKey).getType() === 'LINK';
  }, callback);
}

const Link = ({ contentState, entityKey, decoratedText, children }: LinkType) => {
  const { url } = contentState.getEntity(entityKey).getData();
  return <a href={url}>{decoratedText || children}</a>;
};

interface IRichTextEditor {
  isDisabled?: boolean;
  placeholder?: string;
  setContextValue: (data: any) => void;
  value?: any;
  hideControls?: boolean;
}

export function RichTextEditor({
  value,
  isDisabled = false,
  placeholder = '',
  setContextValue,
  hideControls = false,
}: IRichTextEditor) {
  const decorator = new CompositeDecorator([
    {
      strategy: findLinkEntities,
      component: Link,
    },
  ]);
  const [editorState, setEditorState] = useConvertEditorState(value);

  const [urlState, setUrlState] = useState<URLValue>({
    showURLInput: false,
    urlValue: { text: '', link: '' },
  });

  const [isEditorFocused, setIsEditorFocused] = useState(false);
  const handleFocus = () => setIsEditorFocused(true);
  const handleBlur = () => {
    const editorContent = editorState.getCurrentContent();
    const rawContent = convertToRaw(editorContent);
    setContextValue(JSON.stringify(rawContent));
    setIsEditorFocused(false);
  };

  // ADD LINK
  const updateLink = (e: MouseEvent<HTMLElement>) => {
    e.preventDefault();
    const selection = editorState.getSelection();
    if (!selection.isCollapsed()) {
      const contentState = editorState.getCurrentContent();
      const startKey = selection.getStartKey();
      const startOffset = selection.getStartOffset();
      const blockWithLinkAtBeginning = contentState.getBlockForKey(startKey);
      const linkKey = blockWithLinkAtBeginning.getEntityAt(startOffset);
      let link = '';
      let text = '';
      const anchorKey = selection.getAnchorKey();
      const currentBlock = contentState.getBlockForKey(anchorKey);
      const endOffset = selection.getEndOffset();
      const selectedText = currentBlock.getText().slice(startOffset, endOffset);
      text = selectedText;
      if (linkKey) {
        const linkInstance = contentState.getEntity(linkKey);
        text = linkInstance.getData().text;
        link = linkInstance.getData().url;
      }
      setUrlState({ showURLInput: true, urlValue: { text, link } });
    }
  };

  const confirmLink = (e: MouseEvent<HTMLElement>) => {
    e.preventDefault();
    const contentState = editorState.getCurrentContent();
    const contentStateWithEntity = contentState.createEntity('LINK', 'MUTABLE', {
      text: urlState.urlValue.text,
      url: urlState.urlValue.link,
    });
    const entityKey = contentStateWithEntity.getLastCreatedEntityKey();
    const textWithEntity = Modifier.replaceText(
      contentState,
      editorState.getSelection(),
      urlState.urlValue.text,
      editorState.getCurrentInlineStyle(),
      entityKey,
    );
    const newState = EditorState.createWithContent(textWithEntity, decorator);
    setEditorState(newState);
    setUrlState({ showURLInput: false, urlValue: { text: '', link: '' } });
  };

  const removeLink = (e: MouseEvent<HTMLElement>) => {
    e.preventDefault();
    const selection = editorState.getSelection();
    if (!selection.isCollapsed()) {
      setEditorState(RichUtils.toggleLink(editorState, selection, null));
    }
  };

  const onURLChange = (e: ChangeEvent<HTMLInputElement>) =>
    setUrlState((prev) => ({
      ...prev,
      urlValue: { ...prev.urlValue, [e.target.name]: e.target.value },
    }));

  const closeLinkDialog = () => setUrlState((prev) => ({ ...prev, showURLInput: false }));

  // ADD STYLES
  const updateStyle = (style: string) => {
    const nextState = RichUtils.toggleInlineStyle(editorState, style);
    setEditorState(nextState);
  };

  return (
    <>
      <Grid
        sx={(theme) => ({
          minHeight: '112px',
          outline: isEditorFocused
            ? `2px solid ${theme.palette.secondary.main}`
            : `1px solid ${theme.palette.text.subdued}`,
          boxSizing: 'border-box',
          borderRadius: theme.shape.borderRadius,
          color: theme.palette.text.disabled,
          outlineColor: isDisabled ? theme.palette.text.disabled : '',
        })}
      >
        <Box
          sx={(theme) => ({
            backgroundColor: theme.palette.background.main,
            borderBottom: isDisabled ? '0' : theme.borders.main,
            borderTopRightRadius: 1,
            borderTopLeftRadius: 1,
          })}
        >
          {hideControls ? null : (
            <RichTextControls updateStyle={updateStyle} updateLink={updateLink} isDisabled={isDisabled} />
          )}
          <AddLinkDialog
            onURLChange={onURLChange}
            urlState={urlState}
            confirmLink={confirmLink}
            closeLinkDialog={closeLinkDialog}
            removeLink={removeLink}
          />
        </Box>
        <Grid
          sx={{
            marginBottom: '25px',
            padding: '10px',
            '& a': { color: 'secondary.main' },
            color: isDisabled ? 'text.disabled' : 'text.primary',
            fontWeight: 'normal',
            ...containerEditor,
          }}
        >
          <Editor
            spellCheck
            onFocus={handleFocus}
            onBlur={handleBlur}
            editorState={editorState}
            onChange={(state: any) => setEditorState(state)}
            placeholder={placeholder}
            readOnly={isDisabled}
          />
        </Grid>
      </Grid>
    </>
  );
}
